import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthProvider';
import { apiFetch } from '@/lib/api-fetch';

/**
 * Admin Time-travel hooks (#1550 global; #1632 per-org scope).
 *
 * `useTimeTravelOverride()` (global, no args) is mounted by the app shell so
 * the FE banner can render "Simulated date: YYYY-MM-DD" for every user when
 * the override is active. Refetches every 5 minutes so the banner picks up
 * remote toggles without a hard reload — the admin who flips the toggle sees
 * it immediately via the mutation's `onSuccess` invalidation, so a slow
 * background cadence for *other* viewers is fine.
 *
 * #1632 — the backend (#1584) resolves overrides per-org → global → real
 * wall clock, addressed by `?scope=global` or `?scope=org&organizationId=<id>`
 * (omitting `scope` defaults to global, so #1550 callers are unchanged). Pass
 * `{ scope: 'org', organizationId }` to read/write a single org's override;
 * the backend enforces the partner-subtree check (403 outside the subtree).
 */

export type TimeTravelScope = 'global' | 'org';

export interface TimeTravelScopeArg {
  /** Defaults to `'global'` (back-compat with #1550 callers). */
  readonly scope?: TimeTravelScope;
  /** Required when `scope === 'org'`. */
  readonly organizationId?: string | null;
}

export interface TimeOverrideRow {
  active: boolean;
  customDate: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

export const TIME_TRAVEL_QUERY_KEY = ['admin', 'time-travel'] as const;

function scopeQuery(scope: TimeTravelScope, organizationId: string | null): string {
  const params = new URLSearchParams({ scope });
  if (scope === 'org' && organizationId) {
    params.set('organizationId', organizationId);
  }
  return params.toString();
}

export function useTimeTravelOverride(arg: TimeTravelScopeArg = {}) {
  const scope = arg.scope ?? 'global';
  const organizationId = arg.organizationId ?? null;
  const { getAccessToken, isAuthenticated } = useAuth();
  return useQuery({
    // Scope/org are part of the key so each (scope, org) caches independently
    // and the picker re-reads when the selection changes.
    queryKey: [...TIME_TRAVEL_QUERY_KEY, scope, organizationId],
    // An org read needs a concrete org id; don't fire a `scope=org` request
    // with no org (e.g. before the active org resolves).
    enabled: isAuthenticated && (scope === 'global' || organizationId !== null),
    queryFn: () =>
      apiFetch<TimeOverrideRow>(
        `/api/v1/admin/system/time-travel?${scopeQuery(scope, organizationId)}`,
        { getAccessToken },
      ),
    // #1714 — the banner mounts on every route (app shell) and fires this
    // read TWICE (global + org scope). A 60s stale/poll cadence made it the
    // 2nd-worst per-route offender (70× across 22 pages in the 2026-06-25 nav
    // benchmark). Overrides are rare admin toggles, so a 5-min cadence is
    // ample for the passive banner; the admin who flips it gets an immediate
    // refresh via `useUpdateTimeTravelOverride`'s `onSuccess` invalidation.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export interface UpdateTimeTravelInput extends TimeTravelScopeArg {
  active: boolean;
  customDate: string | null;
}

export function useUpdateTimeTravelOverride() {
  const { getAccessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTimeTravelInput) => {
      const scope = input.scope ?? 'global';
      const organizationId = input.organizationId ?? null;
      return apiFetch<TimeOverrideRow>(
        `/api/v1/admin/system/time-travel?${scopeQuery(scope, organizationId)}`,
        {
          method: 'PUT',
          getAccessToken,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            active: input.active,
            customDate: input.customDate,
          }),
        },
      );
    },
    onSuccess: () => {
      // Prefix-invalidate so every cached (scope, org) read refreshes.
      void qc.invalidateQueries({ queryKey: TIME_TRAVEL_QUERY_KEY });
    },
  });
}
